import React, { lazy, Suspense, useEffect, useState } from "react";
import {
  Redirect,
  Route,
  Switch,
  HashRouter,
  useHistory,
} from "react-router-dom";
import Box from "@mui/material/Box";
import PropagateLoader from "react-spinners/PropagateLoader";
import ProtectedRoute from "~src/features/authentication/index";
import Layout from "./components/Layout";
import callAPI from "./utils/callAPI";
import { css } from "@emotion/react";
import { NAVBAR_BLUE } from "./data/constant";

const Home = lazy(() => import("~src/features/dashboard/index"));
const Library = lazy(() => import("~src/features/library/index"));
const WidgetList = lazy(() => import("~src/features/dashboard/components/widget/index"));
const DetailDashboard = lazy(() => import("~src/features/Forecasting/components/dialogueBox"));
const PremiumDashboard = lazy(() => import("~src/features/mockups/Premium/index"));
const Forecasting = lazy(() => import("~src/features/Forecasting/index"));
const Workspace = lazy(() => import("./routes/Workspace"));
const Login = lazy(() => import("./routes/Login"));
const Likelihood = lazy(() => import("./features/mockups/likelihood"));
const ClaimAmountForecast = lazy(() => import("./features/mockups/claimAmountForecast"));
const FraudDetection = lazy(() => import("./features/mockups/fraudDetection"));
const ChurnMember = lazy(() => import("./features/mockups/churn/churnMember"));
const Page = lazy(() => import("./features/dummy/page"));
const UtilizationDashboard = lazy(() => import("~src/features/utilization-dashboard/index"));
const FWADashboard= lazy(() => import("~src/features/fwa-dashboard/index"));
const ContractsDashboard = lazy(() => import("~src/features/contracts-dashboard/index"));
const CustomDashboard = lazy(() => import("~src/features/custom-dashboard/index"));
const ChatbotPage = lazy(() => import("~src/features/chatbot/ChatbotPage"));

const loaderStyles = css`
  display: block;
  margin: auto;
`;

const RenderLoader = () => (
  <div
    className="d-flex align-items-center justify-content-center"
    style={{ width: "100vw", height: "100vh" }}
  >
    <PropagateLoader color={NAVBAR_BLUE} css={loaderStyles} size={15} />
  </div>
);

function useDashboards() {
  const [dashboards, setDashboards] = useState([]);
  const [loading, setLoading] = useState(true);
  const history = useHistory();

  useEffect(() => {
    const loggedInUser = localStorage.getItem("user");
    if (loggedInUser) {
      callAPI("GET", "get_dashboard_list/", "", "")
        .then((response) => {
          setDashboards(response.data || []);
        })
        .catch((error) => {
          console.error("Error fetching dashboards:", error);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
      history.push("/login");
    }
  }, [history]);

  return { dashboards, loading };
}

export default function Router() {
  const { dashboards, loading } = useDashboards();

  if (loading) {
    return <RenderLoader />;
  }

  return (
    <Box sx={{ display: "flex" }}>
      <HashRouter>
        <Suspense fallback={<RenderLoader />}>
          <Switch>
            <Route path="/login" component={Login} />
            <ProtectedRoute path="/widget-generator" component={(props) => <Layout><Library {...props} /></Layout>} />
            <ProtectedRoute path="/workspace" component={(props) => <Layout><Workspace {...props} /></Layout>} />
            <ProtectedRoute path="/WidgetList" component={(props) => <Layout><WidgetList {...props} /></Layout>} />
            <ProtectedRoute path="/utilization-dashboard" component={(props) => <Layout><UtilizationDashboard {...props} /></Layout>} />
            <ProtectedRoute path="/fwa-dashboard" component={(props) => <Layout><FWADashboard {...props} /></Layout>} />
            <ProtectedRoute path="/contracts-dashboard" component={(props) => <Layout><ContractsDashboard {...props} /></Layout>} />
            <ProtectedRoute path="/dashboard/:dashboardId" hideSidebar component={(props) => <Layout><CustomDashboard {...props} /></Layout>} />
            <ProtectedRoute path="/forecasting/dashboard" component={(props) => <Layout><DetailDashboard {...props} /></Layout>} />
            <ProtectedRoute path="/forecasting/premium" component={(props) => <Layout><PremiumDashboard {...props} /></Layout>} />
            <ProtectedRoute path="/forecasting/forecasting" component={(props) => <Layout><Page {...props} /></Layout>} />
            <ProtectedRoute path="/forecasting/fraudDetection" component={(props) => <Layout><FraudDetection {...props} /></Layout>} />
            <ProtectedRoute path="/forecasting/churnMember" component={(props) => <Layout><ChurnMember {...props} /></Layout>} />
            <ProtectedRoute path="/forecasting/likelihood" component={(props) => <Layout><Likelihood {...props} /></Layout>} />
            <ProtectedRoute path="/forecasting/claimAmountForecast" component={(props) => <Layout><ClaimAmountForecast {...props} /></Layout>} />
            <ProtectedRoute path="/test" component={(props) => <Layout><Forecasting {...props} changeColor={true} /></Layout>} />
            <ProtectedRoute path="/chatbot" component={(props) => <Layout><ChatbotPage {...props} /></Layout>} />

            <Route path="/dashboard">
              <Redirect
                to={
                  dashboards.length > 0
                    ? `/dashboard/${dashboards[0].id}_${dashboards[0].name}`
                    : "/utilization-dashboard"
                }
              />
            </Route>

            <Route exact path="/">
              <Redirect to="/utilization-dashboard" />
            </Route>

            <Route render={() => <Redirect to="/utilization-dashboard" />} />
          </Switch>
        </Suspense>
      </HashRouter>
    </Box>
  );
}
